import type { GateMovementDirection } from '@/__generated__/graphql';
import { AU_SCROLLBAR_SX, AU_TEXT_DIM } from '@/theme/au-compat';
import { MovementDataRenderer } from '@features/gateCommon';
import { Trans } from '@lingui/macro';
import { Box, Skeleton, Typography, type SxProps, type Theme } from '@mui/material';
import { type FC, memo, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { List, RowComponentProps } from 'react-window';
import { PanelHeading } from './common';
import { NotificationCard } from './panelNotification/NotificationCard';
import { NotificationData } from './panelNotification/utils';

interface Props {
  movementId: number | null;
  /** Ajustes de layout del contenedor — hoy, el tope propio que el feed recupera apilado. */
  sx?: SxProps<Theme>;
}

/**
 * El alto de una fila: la tarjeta (56, que es lo que miden el glifo y sus dos líneas con el padding
 * de `.oc-notif`) más los 6px de aire de `.oc-notif-list`. `react-window` mide en filas de alto
 * fijo, así que el gap va **dentro** de la fila y no entre filas.
 */
const ROW_HEIGHT = 62;
const ROW_GAP = 6;

const NotificationRow: FC<
  RowComponentProps<{
    messages: NotificationData[];
    direction: GateMovementDirection | null;
    animateFeaturedId: number | null;
    onAnimationDone: () => void;
  }>
> = memo(
  ({ index, style, ariaAttributes, messages, direction, animateFeaturedId, onAnimationDone }) => {
    const notification = messages[index];
    if (!notification) return null;
    const animate = index === 0 && notification.id === animateFeaturedId;
    return (
      /**
       * `ariaAttributes` se propaga, no se descarta.
       *
       * `react-window` pinta su root con `role="list"` y le pasa a cada fila el `role="listitem"` más
       * el `aria-posinset` / `aria-setsize` que un lector necesita para decir «3 de 47». Sin esto, con
       * la lista virtualizada, el lector sólo encuentra los `div` de las filas visibles y pierde las
       * dos cosas: que pertenecen a una lista y cuántas hay en total.
       *
       * Es además la convención del repo — `downloadData/DownloadDataModal.tsx:61`,
       * `reports2/.../MultipleCheckbox.tsx:119` y `reports2/.../3-FormatAndRecipients.tsx:61` ya lo
       * hacen; este feed era el único que no.
       */
      <Box style={style} {...ariaAttributes} sx={{ pb: `${ROW_GAP}px` }}>
        <NotificationCard
          data={notification}
          direction={direction}
          isFeatured={index === 0}
          animate={animate}
          onAnimationEnd={onAnimationDone}
        />
      </Box>
    );
  },
);

const NotificationList: FC<{
  messages: NotificationData[];
  direction: GateMovementDirection | null;
  labelledBy: string;
}> = ({ messages, direction, labelledBy }) => {
  const lastFeaturedIdRef = useRef<number | null>(null);
  const [animateFeaturedId, setAnimateFeaturedId] = useState<number | null>(null);

  const currentId = messages[0]?.id ?? null;

  useEffect(() => {
    if (currentId !== null && currentId !== lastFeaturedIdRef.current) {
      lastFeaturedIdRef.current = currentId;
      setAnimateFeaturedId(currentId);
    }
  }, [currentId]);

  const onAnimationDone = useCallback(() => setAnimateFeaturedId(null), []);

  const rowProps = useMemo(
    () => ({ messages, direction, animateFeaturedId, onAnimationDone }),
    [messages, direction, animateFeaturedId, onAnimationDone],
  );

  return (
    /**
     * `role="log"` y no `status`: el feed es un histórico al que se le **agregan** entradas, y con
     * `aria-relevant="additions"` el lector anuncia sólo la que llegó en vez de releer todo. El
     * nombre sale del heading del panel, que es el rótulo que la vista ya usa.
     *
     * Va en el contenedor del scroll y no en el root de `react-window`, que es suyo y lleva su
     * `role="list"`: un `log` que además es `list` no se puede declarar en el mismo nodo.
     */
    <Box
      role="log"
      aria-live="polite"
      aria-relevant="additions"
      aria-labelledby={labelledBy}
      sx={{ height: '100%', ...AU_SCROLLBAR_SX, '& > div': AU_SCROLLBAR_SX }}
    >
      <List
        rowCount={messages.length}
        rowHeight={ROW_HEIGHT}
        rowComponent={NotificationRow}
        rowProps={rowProps}
        style={{ overflowX: 'hidden' }}
      />
    </Box>
  );
};

/**
 * **El feed no es una card**, y por eso no usa `PanelPaper`: va directo sobre el cuerpo del
 * diálogo. Cada mensaje ya trae su propio fondo teñido, así que envolverlos deja las tarjetas dos
 * veces encajadas y el tono de cada una —que es el dato— compitiendo con la superficie que las
 * contiene.
 */
export const PanelNotifications: FC<Props> = ({ movementId, sx }) => {
  const headingId = useId();

  return (
    <Box
      sx={{
        gridArea: 'notif',
        minWidth: 0,
        minHeight: 0,
        px: '2px',
        display: 'flex',
        flexDirection: 'column',
        ...sx,
      }}
    >
      <PanelHeading id={headingId}>
        <Trans>Operación en tiempo real</Trans>
      </PanelHeading>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        {movementId ? (
          <MovementDataRenderer
            movementId={movementId}
            render={({ movement }) => {
              const messages = movement.messages;
              if (messages.length === 0) {
                return (
                  <Typography
                    sx={{ fontSize: 12, fontStyle: 'italic', color: AU_TEXT_DIM, p: '12px 4px' }}
                  >
                    <Trans>Aún no se han recibido mensajes de la compuerta</Trans>
                  </Typography>
                );
              }
              return (
                <NotificationList
                  messages={messages}
                  direction={movement.direction ?? null}
                  labelledBy={headingId}
                />
              );
            }}
            // Alto completo: un `Skeleton` de una línea dejaba el área del feed casi vacía y el
            // diálogo entero se reacomodaba al llegar los mensajes.
            fallback={() => <Skeleton variant="rounded" sx={{ height: '100%' }} />}
          />
        ) : (
          <Typography sx={{ fontSize: 12, fontStyle: 'italic', color: AU_TEXT_DIM, p: '12px 4px' }}>
            <Trans>Aún no se han recibido mensajes de la compuerta</Trans>
          </Typography>
        )}
      </Box>
    </Box>
  );
};
